// src/utils/enhancedPdfGenerator.js
import jsPDF from 'jspdf'
import 'jspdf-autotable'

export const generateProfessionalPDF = ({
  analysis,
  transcript,
  agent,
  callType,
  industry = "home_improvement",
  score,
  timestamp
}) => {
  const doc = new jsPDF();

  const COLORS = {
    primary: [10, 75, 75],
    secondary: [52, 211, 153],
    text: [31, 41, 55],
    light: [249, 250, 251],
    ok: [34, 197, 94],
    warn: [251, 191, 36],
    bad: [239, 68, 68]
  };

  const resolvedScore = score || extractScoreFromAnalysis(analysis);

  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, 210, 40, "F");
  doc.setFillColor(...COLORS.secondary);
  doc.rect(0, 38, 210, 2, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(24);
  doc.text("NeuroTrain", 20, 20);
  doc.setFont(undefined, "normal");
  doc.setFontSize(12);
  doc.text("AI-Powered Call Analysis Report", 20, 30);

  // Position helpers
  let y = 50;
  const addPageIfNeeded = (limit = 270, reset = 20) => {
    if (y > limit) { 
      addFooter(doc);
      doc.addPage(); 
      y = reset; 
    }
  };

  // Executive Summary Card
  doc.setFillColor(...COLORS.light);
  roundedRectSafe(doc, 15, y, 180, 40, 3, 3, "F");

  const band = resolvedScore >= 8 ? COLORS.ok : (resolvedScore >= 6 ? COLORS.warn : COLORS.bad);
  doc.setFillColor(...band);
  roundedRectSafe(doc, 20, y + 5, 30, 30, 2, 2, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(20);
  doc.text(String(resolvedScore || 0), 35, y + 25, { align: "center" });
  doc.setFontSize(10);
  doc.text("/10", 35, y + 32, { align: "center" });

  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, "normal");
  doc.setFontSize(11);
  doc.text(`Agent: ${agent || "Not specified"}`, 60, y + 15);
  doc.text(`Call Type: ${formatCallType(callType)}`, 60, y + 23);
  doc.text(`Industry: ${getIndustryName(industry)}`, 60, y + 31);
  doc.text(`Date: ${timestamp || new Date().toLocaleString()}`, 120, y + 15);
  doc.text(`Grade: ${getGrade(resolvedScore)}`, 120, y + 23);

  y += 50;

  // Section header helper
  const sectionHeader = (title) => {
    addPageIfNeeded(250, 20);
    doc.setFillColor(...COLORS.primary);
    doc.rect(15, y, 180, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    doc.text(title, 20, y + 6);
    y += 12;
  };

  // Paragraph helper
  const paragraph = (text, width = 175, lh = 5) => {
    if (!text) return;
    doc.setTextColor(...COLORS.text);
    doc.setFont(undefined, "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(String(text), width);
    for (const ln of lines) {
      addPageIfNeeded();
      doc.text(ln, 20, y);
      y += lh;
    }
    y += 3;
  };

  // Render analysis sections
  const sections = parseAnalysisSections(analysis);
  const order = [
    "Executive Summary",
    "Strengths Identified",
    "Critical Improvements Needed",
    "Qualification Checklist",
    "Detailed Category Scores",
    "Rebuttal Analysis",
    "Missed Opportunities",
    "Training Recommendations",
    "Example Script Improvements"
  ];
  
  const rendered = new Set();
  for (const title of order) {
    if (sections[title]) {
      sectionHeader(title);
      paragraph(sections[title]);
      rendered.add(title);
    }
  }
  
  // Add any remaining sections not in the order list
  for (const [title, content] of Object.entries(sections)) {
    if (rendered.has(title) || !content || !content.trim()) continue;
    sectionHeader(title);
    paragraph(content);
  }

  // Transcript Section (new page)
  addFooter(doc);
  doc.addPage();
  y = 20;
  sectionHeader("Call Transcript");

  const msgs = parseTranscript(transcript);
  
  if (msgs.length > 0) {
    for (const msg of msgs) {
      addPageIfNeeded(260, 20);
      
      const ribbon = msg.type === "agent" ? [219, 234, 254] : [243, 244, 246];
      doc.setFillColor(...ribbon);
      roundedRectSafe(doc, 15, y - 3, 180, 6, 1, 1, "F");
      
      doc.setFont(undefined, "bold");
      doc.setFontSize(9);
      doc.setTextColor(...(msg.type === "agent" ? COLORS.primary : COLORS.text));
      doc.text(String(msg.speaker || "").toUpperCase(), 20, y);
      y += 5;

      doc.setFont(undefined, "normal");
      doc.setTextColor(...COLORS.text);
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(String(msg.text || ""), 170);
      for (const ln of lines) {
        addPageIfNeeded();
        doc.text(ln, 25, y);
        y += 4;
      }
      y += 3;
    }
  } else {
    // Fallback to raw transcript if no parsed messages
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(transcript || "No transcript available", 170);
    for (const ln of lines) {
      addPageIfNeeded();
      doc.text(ln, 20, y);
      y += 5;
    }
  }

  // Add footers to all pages
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { 
    doc.setPage(i); 
    addProfessionalFooter(doc, i, pages); 
  }

  const safeAgent = String(agent || "Agent").replace(/[^\w\-]+/g, "_");
  const fileName = `NeuroTrain_Analysis_${safeAgent}_${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(fileName);
}

// Helper functions
function addFooter(doc) {
  const pageNumber = doc.internal.getNumberOfPages();
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  
  // Left footer
  doc.text('NeuroTrain Call Analyzer - Powered by AI', 14, 285);
  
  // Center footer
  doc.text(new Date().toLocaleDateString(), 105, 285, { align: 'center' });
  
  // Right footer
  doc.text(`Page ${pageNumber}`, 196, 285, { align: 'right' });
}

function parseAnalysisSections(analysis) {
  const sections = {};
  if (!analysis) return sections;
  
  const lines = analysis.split('\n');
  let currentSection = null;
  let currentContent = [];
  
  lines.forEach(line => {
    // Check for section headers (### or ##)
    if (line.match(/^###?\s+(.+)/)) {
      if (currentSection) {
        sections[currentSection] = currentContent.join('\n').trim();
      }
      currentSection = line.replace(/^###?\s+/, '').trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  });
  
  // Don't forget the last section
  if (currentSection) {
    sections[currentSection] = currentContent.join('\n').trim();
  }
  
  return sections;
}

function formatTranscript(transcript) {
  if (!transcript) return [];
  
  const lines = transcript.split('\n');
  const formatted = [];
  
  lines.forEach(line => {
    // Clean up the line
    line = line.trim();
    if (!line) return;
    
    // Format speaker lines
    if (line.match(/^\*?\*?(Agent|Rep|Sales)/i)) {
      formatted.push('Agent: ' + line.replace(/^\*?\*?(Agent|Rep|Sales)[\s:]*\*?\*?/i, ''));
    } else if (line.match(/^\*?\*?(Customer|Client|Prospect)/i)) {
      formatted.push('Customer: ' + line.replace(/^\*?\*?(Customer|Client|Prospect)[\s:]*\*?\*?/i, ''));
    } else if (line !== '## Transcript') {
      formatted.push(line);
    }
  });
  
  return formatted;
}

function extractScoreFromAnalysis(analysis) {
  if (!analysis) return 0;
  const match = analysis.match(/Overall Score:?\s*(\d+(?:\.\d+)?)/i);
  return match ? parseFloat(match[1]) : 0;
}

function getGrade(score) {
  if (score >= 9) return 'A+';
  if (score >= 8) return 'A';
  if (score >= 7) return 'B';
  if (score >= 6) return 'C';
  if (score >= 5) return 'D';
  return 'F';
}

function getScoreColor(score) {
  if (score >= 8) return [0, 128, 0]; // Green
  if (score >= 6) return [255, 165, 0]; // Orange
  return [255, 0, 0]; // Red
}

function formatCallType(callType) {
  const types = {
    cold: 'Cold',
    warm: 'Warm',
    rehash: 'Rehash',
    confirmation: 'Confirmation'
  };
  return types[callType] || (callType ? callType.charAt(0).toUpperCase() + callType.slice(1) : 'General');
}

function getIndustryName(industry) {
  const industries = {
    home_improvement: 'Home Improvement',
    solar: 'Solar & Energy',
    insurance: 'Insurance'
  };
  return industries[industry] || 'General';
}

function roundedRectSafe(doc, x, y, w, h, rx, ry, style) {
  if (doc && typeof doc.roundedRect === "function") {
    try { 
      doc.roundedRect(x, y, w, h, rx, ry, style); 
      return; 
    } catch(e) {
      // Fallback to regular rect if roundedRect fails
    }
  }
  doc.rect(x, y, w, h, style || "S");
}

function parseTranscript(transcript) {
  if (!transcript) return [];
  const out = [];
  const lines = String(transcript).split('\n');
  
  for (let raw of lines) {
    let line = String(raw || '').trim();
    if (!line) continue;
    
    line = line.replace(/^\*\*(Agent|Rep|Sales)\*\*\s*:\s*/i, 'Agent: ')
               .replace(/^\*\*(Customer|Client|Prospect)\*\*\s*:\s*/i, 'Customer: ');
    
    const am = line.match(/^(Agent|Rep|Sales)\s*:\s*(.+)$/i);
    const cm = line.match(/^(Customer|Client|Prospect)\s*:\s*(.+)$/i);
    
    if (am) {
      out.push({ type: 'agent', speaker: 'Agent', text: am[2] });
    } else if (cm) {
      out.push({ type: 'customer', speaker: 'Customer', text: cm[2] });
    } else if (line && !line.includes('## Transcript')) {
      // If we have existing messages, append to the last one
      // Otherwise create a new one as agent (default)
      if (out.length > 0) {
        out[out.length - 1].text += ' ' + line;
      } else {
        out.push({ type: 'agent', speaker: 'Agent', text: line });
      }
    }
  }
  return out;
}

function addProfessionalFooter(doc, currentPage, totalPages) {
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.setDrawColor(229, 231, 235);
  doc.line(15, 280, 195, 280);
  doc.text('© 2025 NeuroTrain by Patagon Consulting Inc.', 15, 287);
  doc.text(`Page ${currentPage} of ${totalPages}`, 105, 287, { align: 'center' });
  doc.text('Confidential', 195, 287, { align: 'right' });
}

export default generateProfessionalPDF;
